import { Temporal } from 'temporal-polyfill';

import type { PromoOffer } from '@/server/initState/getPromoOffer';

const isString = (value: unknown): value is string => typeof value === 'string';
const isNullableString = (value: unknown): value is string | null => value === null || isString(value);
// An instant must carry a zone designator; a date-only string is not one.
const isInstant = (value: unknown): value is string => {
	if (!isString(value)) return false;
	try {
		Temporal.Instant.from(value);
		return true;
	} catch {
		return false;
	}
};

// The destination lands in an href and in router.push, so only a same-origin path or an
// absolute http(s) url is allowed; a javascript:/data: scheme or a protocol-relative host
// (`//host`, and `/\host`, which browsers parse the same way) rejects the whole offer.
const isSafeDestination = (value: unknown): value is string => {
	if (!isString(value) || value === '') return false;
	// Browsers strip ASCII tab/CR/LF before resolving an href, so checking the raw string
	// would miss e.g. "/\t/evil.example" resolving to a protocol-relative host on click.
	const normalized = value.replace(/[\t\n\r]/g, '');
	if (normalized === '') return false;
	if (normalized.startsWith('/')) return !normalized.startsWith('//') && !normalized.startsWith('/\\');
	try {
		const { protocol } = new URL(normalized);
		return protocol === 'http:' || protocol === 'https:';
	} catch {
		return false;
	}
};

// The banner dereferences every one of these fields while rendering, so a payload that
// is truthy but incomplete has to be treated as "no offer" rather than reach the component.
export function isPromoOffer(value: unknown): value is PromoOffer {
	if (typeof value !== 'object' || value === null) return false;
	const offer = value as Record<string, unknown>;
	const analytics = offer.analytics as Record<string, unknown> | undefined;
	return (
		isString(offer.id) &&
		isString(offer.period) &&
		isInstant(offer.starts_at) &&
		isInstant(offer.ends_at) &&
		isString(offer.surface) &&
		isSafeDestination(offer.destination_url) &&
		isNullableString(offer.coupon) &&
		isString(offer.title) &&
		isNullableString(offer.subtitle) &&
		isString(offer.cta_label) &&
		isNullableString(offer.dismiss_label) &&
		(offer.variant === undefined || isNullableString(offer.variant)) &&
		typeof analytics === 'object' &&
		analytics !== null &&
		isString(analytics.pack_class) &&
		isString(analytics.flavor)
	);
}
